import { useState, useRef } from 'react';
import { rfwTvTabs, rfwTvContent, type RfwTvCard } from '@/mocks/rfwTvLanding';

export default function RfwTvSection() {
  const [activeTab, setActiveTab] = useState('en-vivo');
  const scrollRef = useRef<HTMLDivElement>(null);

  const filteredContent = rfwTvContent.filter((c) => c.tabId === activeTab);
  const activeTabData = rfwTvTabs.find((t) => t.id === activeTab);

  const scrollTabs = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = 200;
      scrollRef.current.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    }
  };

  return (
    <section id="rfw-tv" className="relative py-20 md:py-28 overflow-hidden bg-background-100">
      {/* Ambient elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[350px] h-[350px] bg-rose-500/3 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-accent-500/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold mb-5">
            <i className="ri-tv-line text-xs"></i>
            <span>RFW TV</span>
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-[1.1]">
            Mirá, descubrí y{' '}
            <span className="text-rose-600">conectate</span>
          </h2>
          <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            El canal oficial de RadioFrecuenciaWeb. Programas en vivo, entrevistas exclusivas, clips virales y los rankings del momento. Siempre al aire.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="relative mb-10">
          {/* Left arrow */}
          <button
            onClick={() => scrollTabs('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-950 hover:border-background-300/60 transition-all cursor-pointer shadow-sm md:flex hidden"
            aria-label="Desplazar tabs izquierda"
          >
            <i className="ri-arrow-left-s-line text-sm"></i>
          </button>

          <div
            ref={scrollRef}
            className="flex items-center gap-2 overflow-x-auto scrollbar-hide px-1 py-1 mx-0 md:mx-10"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {rfwTvTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const count = rfwTvContent.filter((c) => c.tabId === tab.id).length;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer ${
                    isActive
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:text-foreground-950 hover:border-background-300/60'
                  }`}
                >
                  <i className={`${tab.icon} text-sm ${isActive ? '' : 'group-hover:text-foreground-950'}`}></i>
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      isActive ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Right arrow */}
          <button
            onClick={() => scrollTabs('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-950 hover:border-background-300/60 transition-all cursor-pointer shadow-sm md:flex hidden"
            aria-label="Desplazar tabs derecha"
          >
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
        </div>

        {/* Content Area */}
        {filteredContent.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {filteredContent.map((card) => (
              <TvCard key={card.id} card={card} />
            ))}
          </div>
        ) : (
          /* Fallback: nunca pantalla negra ni error — siempre RFW TV */
          <FallbackCard activeTab={activeTabData} />
        )}
      </div>
    </section>
  );
}

function TvCard({ card }: { card: RfwTvCard }) {
  return (
    <div className="group relative rounded-2xl border border-background-200/70 bg-background-50 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-rose-500/5 hover:border-background-300/60 cursor-pointer">
      {/* Thumbnail 16:9 */}
      <div className="relative w-full aspect-video overflow-hidden">
        <img
          src={card.imageUrl}
          alt={card.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

        {/* Play button overlay on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/90 backdrop-blur-sm text-foreground-950 transition-transform duration-300 group-hover:scale-110">
            <i className={`${card.isLive ? 'ri-radio-button-line text-rose-500' : 'ri-play-fill text-lg ml-0.5'}`}></i>
          </div>
        </div>

        {/* Badges */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {card.badge && card.badgeColor && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold text-white ${card.badgeColor}`}>
              {card.isLive && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>}
              {card.badge}
            </span>
          )}
        </div>

        {/* Duration badge */}
        {card.duration && (
          <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-black/70 text-white text-[10px] font-semibold backdrop-blur-sm">
            {card.duration}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h4 className="text-sm font-bold text-foreground-950 line-clamp-1 group-hover:text-rose-600 transition-colors">
          {card.title}
        </h4>
        <p className="text-[11px] text-foreground-500 mt-1 line-clamp-1">{card.subtitle}</p>

        {/* Meta */}
        {card.meta && (
          <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-background-200/70 text-[10px] text-foreground-400">
            {card.metaIcon && <i className={`${card.metaIcon} text-[9px]`}></i>}
            <span className="font-medium">{card.meta}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function FallbackCard({ activeTab }: { activeTab?: { label: string; icon: string } }) {
  return (
    <div className="max-w-2xl mx-auto text-center">
      <div className="relative rounded-3xl border-2 border-dashed border-rose-300/60 bg-rose-50/50 p-10 md:p-14">
        {/* Icon */}
        <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-2xl bg-rose-500 text-white mb-6">
          <i className={`${activeTab?.icon || 'ri-tv-line'} text-3xl`}></i>
        </div>

        <h3 className="text-xl md:text-2xl font-extrabold text-foreground-950 mb-2">
          RFW TV
        </h3>
        <p className="text-sm text-foreground-500 mb-1 font-medium">
          {activeTab ? activeTab.label : 'Contenido'}
        </p>
        <p className="text-xs text-foreground-400 max-w-md mx-auto leading-relaxed">
          Próximamente encontrarás contenido exclusivo en esta sección. Mientras tanto, explorá las otras categorías de RFW TV.
        </p>

        {/* Decorative dots */}
        <div className="flex items-center justify-center gap-2 mt-6">
          <div className="w-2 h-2 rounded-full bg-rose-300"></div>
          <div className="w-2 h-2 rounded-full bg-rose-400"></div>
          <div className="w-2 h-2 rounded-full bg-rose-500"></div>
        </div>
      </div>
    </div>
  );
}